import { parsePlace } from '@guildite/domain/places';
import { visiblePosts } from './blocks.ts';
import type {
  AuthorView, BlockFilter, CommentView, DirectMessage, FollowPerson, FollowTarget, NotificationView, PostView, SocialPort, TaggedListing, ThreadView,
} from './ports.ts';
import { chatExtras, quote } from './chat.ts';
import { sharedPost } from './guilds.ts';
import type { WorkerClient } from './worker.ts';

type Row = { id: string; data: Record<string, unknown> };
type Op = '==' | 'in' | 'array-contains';
type QueryOptions = { filters?: [string, Op, unknown][]; orderBy?: [string, 'asc' | 'desc']; limit?: number };

export type SocialSources = {
  worker: WorkerClient;
  currentUid: () => string | null;
  readDocument: (collection: string, id: string) => Promise<Record<string, unknown> | null>;
  query: (collection: string, options: QueryOptions) => Promise<Row[]>;
  count: (collection: string, filters: [string, Op, unknown][]) => Promise<number>;
  watchQuery: (collection: string, options: QueryOptions, onData: (rows: Row[]) => void, onError: (error: Error) => void) => () => void;
  watchDocument: (path: string, onData: (data: Record<string, unknown> | null) => void, onError: (error: Error) => void) => () => void;
  watchTyping: (threadId: string, listener: (typing: Record<string, number>) => void) => () => void;
  markTyping: (threadId: string, uid: string) => Promise<void>;
  trackPresence: (uid: string, showActive: boolean) => () => void;
  watchActive: (uid: string, listener: (active: { online: boolean; lastActive: number } | null) => void) => () => void;
  readShowActive: (uid: string) => Promise<boolean>;
  // Blocks (data/blocks.ts): posts and comments by these people are left out.
  hidden?: () => Promise<BlockFilter>;
};

const text = (value: unknown) => (typeof value === 'string' ? value : '');
const media = (value: unknown) => {
  const url = text(value);
  return url.startsWith('https://') || url.startsWith('http://127.0.0.1:') ? url : null;
};

function videoOf(value: unknown): PostView['video'] {
  const data = (value ?? {}) as { url?: unknown; posterUrl?: unknown };
  const url = media(data.url);
  return url ? { url, posterUrl: media(data.posterUrl) } : null;
}

function author(value: unknown): AuthorView {
  const data = (value ?? {}) as Record<string, unknown>;
  return { name: text(data.name) || 'Guildite member', handle: text(data.handle) || null, photoUrl: media(data.photoUrl) };
}

export function postFromRecord(id: string, data: Record<string, unknown>): PostView {
  const tags = Array.isArray(data.taggedListings) ? data.taggedListings as Record<string, unknown>[] : [];
  return {
    id, authorId: text(data.authorId), authorKey: text(data.authorKey), asShopId: text(data.asShopId) || null, author: author(data.author),
    caption: text(data.caption), area: text(data.area) || null, place: parsePlace(data.place), photos: (Array.isArray(data.photos) ? data.photos : []).map(media).filter((url): url is string => Boolean(url)),
    video: videoOf(data.video),
    taggedListings: tags.map((tag): TaggedListing => ({
      id: text(tag.id), name: text(tag.name) || 'Listing', priceMinor: Math.round(Number(tag.priceMinor) || 0), photo: media(tag.photo), shopName: text(tag.shopName), category: text(tag.category),
    })).filter(tag => tag.id),
    taggedPeople: (Array.isArray(data.taggedPeople) ? data.taggedPeople as Record<string, unknown>[] : [])
      .map(person => ({ uid: text(person?.uid), handle: text(person?.handle), name: text(person?.name) || 'Guildite member', photoUrl: media(person?.photoUrl) }))
      .filter(person => person.uid && person.handle),
    likeCount: Math.max(0, Number(data.likeCount) || 0), commentCount: Math.max(0, Number(data.commentCount) || 0), createdAt: text(data.createdAt),
  };
}

function newestFirst<T extends { createdAt: string }>(items: T[]): T[] {
  return [...items].sort((left, right) => right.createdAt.localeCompare(left.createdAt));
}

const FEED_SIZE = 60;
const FOLLOW_LIST_SIZE = 100;

export function createSocial(sources: SocialSources): SocialPort {
  const { worker, currentUid, readDocument, query, count, watchQuery, hidden } = sources;
  const shown = async (posts: PostView[]) => (hidden ? visiblePosts(posts, await hidden()) : posts);
  const active = (rows: Row[]) => shown(rows.map(row => postFromRecord(row.id, row.data)));

  async function recent(): Promise<PostView[]> {
    return active(await query('posts', { filters: [['status', '==', 'active']], orderBy: ['createdAt', 'desc'], limit: FEED_SIZE }));
  }

  return {
    recent,
    async feed() {
      const uid = currentUid();
      if (!uid) return { posts: await recent(), following: false };
      const follows = await query('follows', { filters: [['followerId', '==', uid]], limit: 300 });
      const keys = [`user:${uid}`, ...follows.map(row => text(row.data.targetKey)).filter(Boolean)];
      if (keys.length === 1) return { posts: await recent(), following: false };
      const chunks: string[][] = [];
      for (let index = 0; index < keys.length; index += 10) chunks.push(keys.slice(index, index + 10));
      const pages = await Promise.all(chunks.map(chunk => query('posts', {
        filters: [['authorKey', 'in', chunk], ['status', '==', 'active']], orderBy: ['createdAt', 'desc'], limit: FEED_SIZE,
      })));
      return { posts: newestFirst(await active(pages.flat())).slice(0, FEED_SIZE), following: true };
    },
    // Equality filters only, so no composite index is needed; newest first here.
    postsInPlace: async key => newestFirst(await active(await query('posts', { filters: [['placeKey', '==', key], ['status', '==', 'active']], limit: FEED_SIZE }))),
    postsBy: async authorKey => active(await query('posts', { filters: [['authorKey', '==', authorKey], ['status', '==', 'active']], orderBy: ['createdAt', 'desc'], limit: 60 })),
    async post(postId) {
      const data = await readDocument('posts', postId).catch(() => null);
      if (!data || data.status !== 'active') return null;
      return (await shown([postFromRecord(postId, data)]))[0] ?? null;
    },
    async likedIds(postIds) {
      const uid = currentUid();
      if (!uid) return new Set();
      const checks = await Promise.all(postIds.map(async id => ((await readDocument(`posts/${id}/likes`, uid).catch(() => null)) ? id : null)));
      return new Set(checks.filter((id): id is string => Boolean(id)));
    },
    createPost: input => worker.call('create_post', { ...input, submissionKey: crypto.randomUUID() }),
    updatePost: async (postId, patch) => { await worker.call('update_post', { postId, ...patch }); },
    removeMyTag: async postId => { await worker.call('remove_post_tag', { postId }); },
    postsTagging: async uid => active(await query('posts', { filters: [['taggedUids', 'array-contains', uid], ['status', '==', 'active']], orderBy: ['createdAt', 'desc'], limit: 60 })),
    deletePost: async postId => { await worker.call('delete_post', { postId }); },
    setLike: async (postId, liked) => { await worker.call('set_like', { postId, liked }); },
    watchComments(postId, listener, onError) {
      // Comments by blocked people are left out once the block list is in.
      let blockedUsers: ReadonlySet<string> = new Set();
      let last: CommentView[] | null = null;
      const emit = () => { if (last) listener(last.filter(comment => !blockedUsers.has(comment.authorId))); };
      hidden?.().then(filter => { blockedUsers = filter.users; emit(); }, () => undefined);
      return watchQuery('comments', {
        filters: [['postId', '==', postId], ['status', '==', 'active']], orderBy: ['createdAt', 'asc'], limit: 300,
      }, rows => {
        last = rows.map(({ id, data }): CommentView => ({
          id, postId, authorId: text(data.authorId), author: author(data.author), text: text(data.text), createdAt: text(data.createdAt), scamWarning: data.scamWarning === true,
        }));
        emit();
      }, onError);
    },
    addComment: (postId, value) => worker.call('add_comment', { postId, text: value }),
    deleteComment: async commentId => { await worker.call('delete_comment', { commentId }); },
    async isFollowing(target) {
      const uid = currentUid();
      return Boolean(uid && await readDocument('follows', `${uid}_${target.type}_${target.id}`).catch(() => null));
    },
    setFollow: async (target, following) => { await worker.call('set_follow', { targetType: target.type, targetId: target.id, following }); },
    async followCounts(target) {
      const [followers, following] = await Promise.all([
        count('follows', [['targetKey', '==', `${target.type}:${target.id}`]]),
        target.type === 'user' ? count('follows', [['followerId', '==', target.id]]) : Promise.resolve(null),
      ]);
      return { followers, following };
    },
    async followList(target, which) {
      if (which === 'following' && target.type !== 'user') return { people: [], total: 0 };
      const filter: [string, Op, unknown] = which === 'followers' ? ['targetKey', '==', `${target.type}:${target.id}`] : ['followerId', '==', target.id];
      const [rows, total] = await Promise.all([query('follows', { filters: [filter], limit: FOLLOW_LIST_SIZE }), count('follows', [filter])]);
      // Names and photos come from the public profile or shop, so they stay current.
      const people = await Promise.all(rows.map(({ data }) => (which === 'followers'
        ? followPerson(readDocument, { type: 'user', id: text(data.followerId) })
        : followPerson(readDocument, { type: data.targetType === 'shop' ? 'shop' : 'user', id: text(data.targetId) }))));
      return { people: people.filter((person): person is FollowPerson => Boolean(person)), total };
    },
    async followsMe(uid) {
      const me = currentUid();
      return Boolean(me && me !== uid && await readDocument('follows', `${uid}_user_${me}`).catch(() => null));
    },
    watchThreads(listener, onError, shopIds = []) {
      const uid = currentUid();
      if (!uid) {
        listener([]);
        return () => {};
      }
      // One live list per source (yours, then each shop's), merged newest first.
      const lists = new Map<string, ThreadView[]>();
      const emit = () => listener([...lists.values()].flat().sort((left, right) => right.updatedAt.localeCompare(left.updatedAt)));
      const stops = [
        watchQuery('threads', { filters: [['participants', 'array-contains', uid]], orderBy: ['updatedAt', 'desc'], limit: 100 }, rows => {
          lists.set('mine', rows.map(row => threadFromRecord(row.id, row.data, uid)));
          emit();
        }, onError),
        ...[...new Set(shopIds)].map(shopId => watchQuery('threads', { filters: [['shopId', '==', shopId], ['kind', '==', 'shop']], limit: 100 }, rows => {
          lists.set(`shop:${shopId}`, rows.map(row => threadFromRecord(row.id, row.data, uid)));
          emit();
        }, onError)),
      ];
      return () => stops.forEach(stop => stop());
    },
    watchMessages: (threadId, listener, onError) => watchQuery(`threads/${threadId}/messages`, { orderBy: ['createdAt', 'asc'], limit: 500 },
      rows => listener(rows.map(({ id, data }): DirectMessage => ({
        id, authorId: text(data.authorId), asShop: data.asShop === true, official: data.official === true && data.authorId === 'guildite-support', text: text(data.text), post: sharedPost(data.post), createdAt: text(data.createdAt), scamWarning: data.scamWarning === true,
        replyTo: quote(data.replyTo), ...chatExtras(data),
      }))), onError),
    async thread(threadId) {
      const uid = currentUid();
      const data = uid ? await readDocument('threads', threadId).catch(() => null) : null;
      return data && uid ? threadFromRecord(threadId, data, uid) : null;
    },
    sendMessage: (to, value, options = {}) => worker.call('send_direct_message', { ...to, text: value, ...options }),
    react: async (place, messageId, emoji) => { await worker.call('react_to_message', { ...place, messageId, emoji }); },
    editMessage: async (place, messageId, value) => { await worker.call('edit_message', { ...place, messageId, text: value }); },
    deleteMessage: async (place, messageId) => { await worker.call('delete_message', { ...place, messageId }); },
    deleteMessages: async (place, messageIds) => { await worker.call('delete_messages', { ...place, messageIds }); },
    hideMessages: async (place, messageIds) => { await worker.call('hide_messages', { ...place, messageIds }); },
    openViewOnce: (place, messageId) => worker.call('open_view_once', { ...place, messageId }),
    watchHidden(place, listener) {
      const uid = currentUid();
      if (!uid) { listener(new Set()); return () => undefined; }
      const key = 'threadId' in place ? `dm_${place.threadId}` : `guild_${place.guildId}`;
      return sources.watchDocument(`users/${uid}/hiddenMessages/${key}`, data => {
        listener(new Set(Array.isArray(data?.ids) ? data.ids.filter((id): id is string => typeof id === 'string') : []));
      }, () => listener(new Set()));
    },
    markDelivered: async threadIds => { if (threadIds.length) await worker.call('mark_threads_delivered', { threadIds: threadIds.slice(0, 50) }); },
    markThreadRead: async threadId => { await worker.call('mark_thread_read', { threadId }); },
    watchTyping: (threadId, listener) => sources.watchTyping(threadId, listener),
    async markTyping(threadId) {
      const uid = currentUid();
      if (uid) await sources.markTyping(threadId, uid);
    },
    watchNotifications(listener, onError) {
      const uid = currentUid();
      if (!uid) {
        listener([]);
        return () => {};
      }
      return watchQuery(`users/${uid}/notifications`, { orderBy: ['createdAt', 'desc'], limit: 100 }, rows => listener(rows.map(({ id, data }): NotificationView => ({
        id, type: text(data.type), actor: data.actor ? author(data.actor) : null, text: text(data.text),
        link: text(data.link).startsWith('/') && !text(data.link).startsWith('//') ? text(data.link) : '/notifications',
        createdAt: text(data.createdAt), read: data.read === true,
      }))), onError);
    },
    markNotificationsRead: async () => { await worker.call('mark_notifications_read'); },
    trackPresence(showActive) {
      const uid = currentUid();
      return uid ? sources.trackPresence(uid, showActive) : () => {};
    },
    watchActive: (uid, listener) => sources.watchActive(uid, listener),
    async showActive() {
      const uid = currentUid();
      return uid ? sources.readShowActive(uid).catch(() => true) : true;
    },
  };
}

async function followPerson(readDocument: SocialSources['readDocument'], target: FollowTarget): Promise<FollowPerson | null> {
  if (!target.id) return null;
  const data = await readDocument(target.type === 'shop' ? 'shops' : 'profiles', target.id).catch(() => null);
  if (!data || (target.type === 'shop' && data.status === 'banned')) return null;
  const handle = text(data.handle) || null;
  const view = author({ name: data.name, handle, photoUrl: target.type === 'shop' ? data.logoUrl : data.photoUrl });
  return { ...target, ...view, path: handle ? `/${target.type === 'shop' ? 's' : 'u'}/${encodeURIComponent(handle)}` : null };
}

function threadFromRecord(id: string, data: Record<string, unknown>, uid: string): ThreadView {
  const participants = Array.isArray(data.participants) ? data.participants.map(String) : [];
  const people = (data.people ?? {}) as Record<string, unknown>;
  const last = data.lastMessage as Record<string, unknown> | null;
  const unreadBy = (data.unread ?? {}) as Record<string, unknown>;
  const lastMessage = last ? { text: text(last.text), authorId: text(last.authorId), asShop: last.asShop === true, at: text(last.at) } : null;
  const updatedAt = text(data.updatedAt);
  const readAt = (data.readAt ?? {}) as Record<string, unknown>;
  const deliveredAt = (data.deliveredAt ?? {}) as Record<string, unknown>;
  const seenBy = (side: string) => ({ otherDeliveredAt: text(deliveredAt[side]) || null, otherReadAt: text(readAt[side]) || null });
  if (data.kind === 'shop') {
    const shopAuthor = author(data.shop);
    const shop = { id: text(data.shopId), name: shopAuthor.name, handle: shopAuthor.handle };
    const customerId = text(data.customerId);
    if (customerId === uid) {
      return { id, side: 'customer', shop, other: { uid: '', ...shopAuthor }, lastMessage, unread: Math.max(0, Number(unreadBy[uid]) || 0), updatedAt, ...seenBy('shop') };
    }
    return { id, side: 'shop', shop, other: { uid: customerId, ...author(people[customerId]) }, lastMessage, unread: Math.max(0, Number(unreadBy.shop) || 0), updatedAt, ...seenBy(customerId) };
  }
  const otherUid = data.kind === 'support' ? 'guildite-support' : participants.find(item => item !== uid) ?? '';
  return { id, side: 'person', shop: null, other: { uid: otherUid, ...author(people[otherUid]) }, lastMessage, unread: Math.max(0, Number(unreadBy[uid]) || 0), updatedAt, ...seenBy(otherUid) };
}
